import io from 'socket.io-client';

// 日志辅助函数
const SOCKET_LOG_PREFIX = '[Socket]';
function logWithTime(...args) {
  const time = new Date().toLocaleTimeString('zh-CN', { hour12: false });
  console.log(`${SOCKET_LOG_PREFIX} [${time}]`, ...args);
}

const socketService = {
  socket: null,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,
  reconnectTimeout: 1000,
  connectionCallbacks: [],
  disconnectCallbacks: [],
  errorCallbacks: [],
  pendingMessages: [], // 消息队列：连接断开时暂存待发送的消息
  _debugEvents: true,  // 调试模式：记录所有收发事件
  
  // 连接Socket服务器
  connect(url) {
    if (this.socket && this.socket.connected) {
      logWithTime('Socket已经连接, id:', this.socket.id);
      return;
    }

    // 生产环境自动使用当前域名，开发环境默认localhost:2729
    let socketUrl = url;
    if (!socketUrl) {
      if (process.env.NODE_ENV === 'production') {
        // 自动使用当前页面的协议和域名
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        socketUrl = `${protocol}//${window.location.host}`;
      } else {
        socketUrl = 'http://localhost:2729';
      }
    }
    logWithTime('>>> 正在连接:', socketUrl);
    
    // 创建Socket连接，配置重连策略
    this.socket = io(socketUrl, {
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectTimeout,
      reconnectionDelayMax: 5000,
      autoConnect: true,
      transports: ['websocket', 'polling'], // WebSocket优先，polling兜底
      withCredentials: true
    });
    
    // 监听所有事件（调试用）
    if (this._debugEvents) {
      this.socket.onAny((eventName, ...args) => {
        // 跳过内部事件
        if (['connect', 'disconnect', 'connect_error', 'reconnect_attempt', 'reconnect', 'reconnect_failed', 'error'].includes(eventName)) {
          return;
        }
        logWithTime(`<<< 收到 [${eventName}]`, args.length === 1 ? args[0] : args);
      });
    }
    
    // 监听连接事件
    this.socket.on('connect', () => {
      logWithTime('=== 连接成功, id:', this.socket.id);
      this.reconnectAttempts = 0;
      // 发送队列中待处理的消息
      this._flushPendingMessages();
      // 调用所有注册的连接回调
      this.connectionCallbacks.forEach(callback => callback());
    });
    
    // 监听连接错误
    this.socket.on('connect_error', (error) => {
      logWithTime('*** 连接错误:', error.message);
      this.errorCallbacks.forEach(callback => callback(error));
    });
    
    // 监听重连尝试
    this.socket.on('reconnect_attempt', (attemptNumber) => {
      logWithTime(`*** 重连尝试 #${attemptNumber}`);
      this.reconnectAttempts = attemptNumber;
    });
    
    // 监听重连成功
    this.socket.on('reconnect', (attemptNumber) => {
      logWithTime(`=== 重连成功, 尝试次数: ${attemptNumber}`);
      this.reconnectAttempts = 0;
      this._flushPendingMessages();
    });
    
    // 监听重连失败
    this.socket.on('reconnect_failed', () => {
      logWithTime(`*** 重连失败，已尝试${this.maxReconnectAttempts}次`);
    });
    
    // 监听断开连接事件
    this.socket.on('disconnect', (reason) => {
      logWithTime('--- 连接断开:', reason);
      // 调用所有注册的断开连接回调
      this.disconnectCallbacks.forEach(callback => callback(reason));
    });
    
    // 监听错误事件
    this.socket.on('error', (error) => {
      logWithTime('*** 错误:', error);
    });
  },
  
  // 断开连接
  disconnect() {
    if (this.socket) {
      logWithTime('--- 主动断开连接');
      this.socket.disconnect();
      this.socket = null;
      this.reconnectAttempts = 0;
      this.pendingMessages = [];
      // 清空回调数组
      this.connectionCallbacks = [];
      this.disconnectCallbacks = [];
      this.errorCallbacks = [];
    }
  },
  
  // 注册事件监听器
  on(eventName, callback) {
    if (this.socket) {
      this.socket.on(eventName, callback);
    }
  },
  
  // 移除事件监听器
  off(eventName, callback) {
    if (this.socket) {
      this.socket.off(eventName, callback);
    }
  },
  
  // 发送事件（未连接时自动排队，连接后自动发送）
  emit(eventName, data) {
    if (this.socket && this.socket.connected) {
      logWithTime(`>>> 发送 [${eventName}]`, data);
      this.socket.emit(eventName, data);
    } else {
      logWithTime(`!!! 未连接，消息加入队列 [${eventName}]`, data);
      this.pendingMessages.push({ eventName, data });
    }
  },

  // 发送队列中所有待处理消息
  _flushPendingMessages() {
    if (this.pendingMessages.length === 0) return;
    logWithTime(`>>> 正在发送 ${this.pendingMessages.length} 条排队消息`);
    const messages = [...this.pendingMessages];
    this.pendingMessages = [];
    messages.forEach(({ eventName, data }) => {
      if (this.socket && this.socket.connected) {
        logWithTime(`>>> 排队发送 [${eventName}]`, data);
        this.socket.emit(eventName, data);
      }
    });
  },
  
  // 获取连接状态
  getConnectionStatus() {
    return this.socket ? this.socket.connected : false;
  },
  
  // 用户认证
  authenticate(token) {
    if (this.socket) {
      logWithTime('>>> 发送认证请求');
      this.socket.emit('authenticate', token);
    } else {
      logWithTime('!!! 认证失败：socket 不存在');
    }
  },
  
  // 注册连接成功回调
  onConnect(callback) {
    if (typeof callback === 'function') {
      this.connectionCallbacks.push(callback);
      // 如果已经连接，立即执行回调
      if (this.socket && this.socket.connected) {
        callback();
      }
    }
  },
  
  // 注册断开连接回调
  onDisconnect(callback) {
    if (typeof callback === 'function') {
      this.disconnectCallbacks.push(callback);
    }
  },

  // 注册连接错误回调
  onConnectError(callback) {
    if (typeof callback === 'function') {
      this.errorCallbacks.push(callback);
    }
  },
  
  // 获取当前Socket ID
  getSocketId() {
    return this.socket ? this.socket.id : null;
  },
  
  // 检查是否正在重连
  isReconnecting() {
    return this.socket ? this.socket.io.reconnecting : false;
  },
  
  // 手动触发重连
  reconnect() {
    if (this.socket) {
      this.socket.connect();
    }
  }
};

export default socketService;